import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  Select,
  Skeleton,
  Table,
  Textarea,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QueryErrorState } from "../api/QueryErrorState.js";
import { useApproveHrVacationOnBehalf, useHrVacations } from "../api/use-hr.js";

/** Gestão de férias na visão do RH (FASE-06-T50). */
export function FeriasRhPage() {
  const [departmentId, setDepartmentId] = useState("");
  const [q, setQ] = useState("");
  const [onlyConflicts, setOnlyConflicts] = useState(false);
  const [onlyWaiting, setOnlyWaiting] = useState(false);

  const filters = useMemo(
    () => ({
      ...(departmentId ? { departmentId } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    }),
    [departmentId, q],
  );

  const query = useHrVacations(filters);
  const onBehalf = useApproveHrVacationOnBehalf();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [behalfId, setBehalfId] = useState<string | null>(null);
  const [behalfReason, setBehalfReason] = useState("");

  function errorMessage(err: unknown): string {
    if (err instanceof EvoluaApiError) return err.problem.detail ?? err.message;
    if (err instanceof Error) return err.message;
    return "Falha na operação";
  }

  async function onBehalfConfirm() {
    if (!behalfId || !behalfReason.trim()) return;
    setActionError(null);
    setBusyId(behalfId);
    try {
      await onBehalf.mutateAsync({ id: behalfId, reason: behalfReason.trim() });
      setBehalfId(null);
      setBehalfReason("");
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  if (query.isPending) {
    return (
      <div className="evolua-stack evolua-stack--2" aria-busy="true">
        <Skeleton height="2rem" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  let items = query.data.data;
  if (onlyConflicts) items = items.filter((i) => i.hasConflict);
  if (onlyWaiting) items = items.filter((i) => i.waitingOnManager);

  return (
    <>
      <PageHeader
        title="Férias (RH)"
        lead="Visão consolidada, conflitos por área e aprovação em nome do gestor ausente (motivo auditado)."
      />

      <p className="evolua-muted">
        Total: {query.data.total} · Conflitos: {query.data.conflictCount}
      </p>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      <FilterBar>
        <Select
          label="Departamento"
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          options={[
            { value: "", label: "Todos" },
            { value: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb", label: "RH" },
            { value: "cccccccc-cccc-4ccc-8ccc-cccccccccccc", label: "Operações" },
          ]}
        />
        <SearchField
          label="Busca"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nome ou área"
        />
        <Checkbox
          label="Só conflitos"
          checked={onlyConflicts}
          onChange={(e) => setOnlyConflicts(e.target.checked)}
        />
        <Checkbox
          label="Aguardando gestor"
          checked={onlyWaiting}
          onChange={(e) => setOnlyWaiting(e.target.checked)}
        />
        <Link to="/solicitacoes">Fila de solicitações →</Link>
      </FilterBar>

      {items.length === 0 ? (
        <EmptyState title="Nenhuma férias" description="Ajuste os filtros." />
      ) : (
        <Table
          caption="Férias"
          getRowId={(item) => item.id}
          columns={[
            { id: "who", header: "Colaborador", cell: (item) => item.requesterName },
            {
              id: "period",
              header: "Período",
              cell: (item) =>
                `${item.startDate ?? "?"}–${item.endDate ?? "?"}${
                  item.days != null ? ` (${item.days}d)` : ""
                }`,
            },
            {
              id: "status",
              header: "Status",
              cell: (item) => (
                <>
                  <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
                  {item.waitingOnManager ? " · aguardando gestor" : ""}
                  {item.hasConflict
                    ? ` · conflito com ${item.conflicts.map((c) => c.otherRequesterName).join(", ")}`
                    : ""}
                </>
              ),
            },
            {
              id: "dept",
              header: "Área",
              priority: "optional",
              cell: (item) => item.departmentName,
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) =>
                item.waitingOnManager && item.status === "IN_PROGRESS" ? (
                  <Button
                    type="button"
                    disabled={busyId === item.id}
                    onClick={() => {
                      setBehalfId(item.id);
                      setBehalfReason("");
                      setActionError(null);
                    }}
                  >
                    Aprovar em nome do gestor
                  </Button>
                ) : (
                  "—"
                ),
            },
          ]}
          rows={items}
        />
      )}

      {behalfId ? (
        <Card title="Aprovar em nome do gestor" className="evolua-mt">
          <p>O motivo é obrigatório e fica registrado no histórico (`[on-behalf]`).</p>
          <Textarea
            label="Motivo"
            value={behalfReason}
            onChange={(e) => setBehalfReason(e.target.value)}
            rows={3}
            placeholder="Ex.: gestor em licença médica"
          />
          <div className="evolua-stack evolua-stack--3">
            <Button
              type="button"
              disabled={!behalfReason.trim() || onBehalf.isPending}
              onClick={() => void onBehalfConfirm()}
            >
              Confirmar
            </Button>
            <Button type="button" variant="secondary" onClick={() => setBehalfId(null)}>
              Cancelar
            </Button>
          </div>
        </Card>
      ) : null}
    </>
  );
}
